/**
 * Admin Dashboard Page
 *
 * Overview of platform activity for administrators.
 */

import { useState, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase, normalizeError } from '@/lib/supabaseClient'
import { Link } from 'react-router-dom'
import AdminAnalyticsPanel from '@/components/AdminAnalyticsPanel'
import { exportProjectsToCSV, exportReservationsToCSV, exportAnalyticsToCSV } from '@/lib/csvExport'

// SECURITY FIX: Optimized to eliminate N+1 queries using proper joins
async function fetchAllProjects() {
  try {
    // Single query with joins instead of N+1 sequential queries
    const { data: projects, error: projectsError } = await supabase
      .from('projects')
      .select(`
        *,
        developer:profiles!developer_id(
          full_name,
          company_name,
          email
        ),
        units_count:units(count)
      `)
      .order('created_at', { ascending: false })

    if (projectsError) throw projectsError

    // Transform the count aggregation format for compatibility
    const transformedProjects = projects.map(project => ({
      ...project,
      units_count: [{ count: project.units_count?.length || 0 }]
    }))

    return { data: transformedProjects, error: null }
  } catch (error) {
    return { data: null, error: normalizeError(error) }
  }
}

async function fetchAllUsers() {
  try {
    const { data, error } = await supabase
      .rpc('admin_get_all_profiles')

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    return { data: null, error: normalizeError(error) }
  }
}

// SECURITY FIX: Optimized to eliminate N+1 queries using proper joins
async function fetchAllReservations() {
  try {
    // Single query with nested joins instead of N+1 sequential queries
    const { data: reservations, error: reservationsError } = await supabase
      .from('reservations')
      .select(`
        *,
        buyer:profiles!buyer_id(
          full_name,
          email
        ),
        unit:units(
          unit_number,
          project_id,
          project:projects(
            title,
            developer_id
          )
        )
      `)
      .order('created_at', { ascending: false })

    if (reservationsError) throw reservationsError

    return { data: reservations, error: null }
  } catch (error) {
    return { data: null, error: normalizeError(error) }
  }
}

export default function AdminDashboardPage() {
  const [activeTab, setActiveTab] = useState('overview')
  const [lastUpdated, setLastUpdated] = useState(new Date())
  const queryClient = useQueryClient()

  // Fetch all data with automatic refetching
  const { data: projectsData, isLoading: projectsLoading, refetch: refetchProjects } = useQuery({
    queryKey: ['admin-projects'],
    queryFn: fetchAllProjects,
    refetchInterval: 30000,
    onSuccess: () => setLastUpdated(new Date()),
  })

  const { data: usersData, isLoading: usersLoading, refetch: refetchUsers } = useQuery({
    queryKey: ['admin-users'],
    queryFn: fetchAllUsers,
    refetchInterval: 60000,
    onSuccess: () => setLastUpdated(new Date()),
  })

  const { data: reservationsData, isLoading: reservationsLoading, refetch: refetchReservations } = useQuery({
    queryKey: ['admin-reservations'],
    queryFn: fetchAllReservations,
    refetchInterval: 30000,
    onSuccess: () => setLastUpdated(new Date()),
  })

  // Real-time subscriptions
  useEffect(() => {
    const projectsChannel = supabase
      .channel('admin-projects-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'projects',
        },
        () => {
          queryClient.invalidateQueries(['admin-projects'])
          setLastUpdated(new Date())
        }
      )
      .subscribe()

    const unitsChannel = supabase
      .channel('admin-units-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'units',
        },
        () => {
          queryClient.invalidateQueries(['admin-projects'])
          setLastUpdated(new Date())
        }
      )
      .subscribe()

    const reservationsChannel = supabase
      .channel('admin-reservations-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'reservations',
        },
        () => {
          queryClient.invalidateQueries(['admin-reservations'])
          setLastUpdated(new Date())
        }
      )
      .subscribe()

    const profilesChannel = supabase
      .channel('admin-profiles-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'profiles',
        },
        () => {
          queryClient.invalidateQueries(['admin-users'])
          setLastUpdated(new Date())
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(projectsChannel)
      supabase.removeChannel(unitsChannel)
      supabase.removeChannel(reservationsChannel)
      supabase.removeChannel(profilesChannel)
    }
  }, [queryClient])

  const projects = projectsData?.data || []
  const users = usersData?.data || []
  const reservations = reservationsData?.data || []

  // Calculate stats
  const stats = {
    totalProjects: projects.length,
    activeProjects: projects.filter(p => p.status === 'activated' || p.status === 'published').length,
    totalUsers: users.length,
    buyers: users.filter(u => u.role === 'buyer').length,
    developers: users.filter(u => u.role === 'developer').length,
    totalReservations: reservations.length,
    activeReservations: reservations.filter(r => r.status === 'active').length,
    convertedReservations: reservations.filter(r => r.status === 'converted').length,
  }

  const handleRefreshAll = () => {
    refetchProjects()
    refetchUsers()
    refetchReservations()
  }

  const isRefreshing = projectsLoading || usersLoading || reservationsLoading

  return (
    <div className="bg-secondary-50 min-h-screen">
      <div className="container-custom py-8">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-2">
              Admin Dashboard
            </h1>
            <p className="text-lg text-secondary-600">
              Platform overview and management
            </p>
            <p className="text-sm text-secondary-500 mt-1">
              Last updated: {lastUpdated.toLocaleTimeString('en-NZ', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
              })}
            </p>
          </div>
          <button
            onClick={handleRefreshAll}
            disabled={isRefreshing}
            className="btn-primary px-4 py-2 flex items-center gap-2"
            title="Refresh all data"
          >
            <svg
              className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="card p-6">
            <div className="text-sm text-secondary-600 mb-1">Total Projects</div>
            <div className="text-3xl font-bold text-secondary-900">{stats.totalProjects}</div>
            <div className="text-sm text-success-600 mt-1">{stats.activeProjects} active</div>
          </div>

          <div className="card p-6">
            <div className="text-sm text-secondary-600 mb-1">Total Users</div>
            <div className="text-3xl font-bold text-secondary-900">{stats.totalUsers}</div>
            <div className="text-sm text-secondary-600 mt-1">
              {stats.buyers} buyers • {stats.developers} developers
            </div>
          </div>

          <div className="card p-6">
            <div className="text-sm text-secondary-600 mb-1">Active Reservations</div>
            <div className="text-3xl font-bold text-success-600">{stats.activeReservations}</div>
            <div className="text-sm text-secondary-600 mt-1">of {stats.totalReservations} total</div>
          </div>

          <div className="card p-6">
            <div className="text-sm text-secondary-600 mb-1">Conversions</div>
            <div className="text-3xl font-bold text-primary-600">{stats.convertedReservations}</div>
            <div className="text-sm text-secondary-600 mt-1">
              {stats.totalReservations > 0
                ? `${Math.round((stats.convertedReservations / stats.totalReservations) * 100)}%`
                : '0%'}{' '}
              rate
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 mb-6 border-b border-secondary-200">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-6 py-3 font-medium text-sm transition-colors border-b-2 ${
              activeTab === 'overview'
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-secondary-600 hover:text-secondary-900'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('projects')}
            className={`px-6 py-3 font-medium text-sm transition-colors border-b-2 ${
              activeTab === 'projects'
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-secondary-600 hover:text-secondary-900'
            }`}
          >
            Projects
            <span className="ml-2 badge badge-primary">{projects.length}</span>
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`px-6 py-3 font-medium text-sm transition-colors border-b-2 ${
              activeTab === 'users'
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-secondary-600 hover:text-secondary-900'
            }`}
          >
            Users
            <span className="ml-2 badge badge-primary">{users.length}</span>
          </button>
          <button
            onClick={() => setActiveTab('reservations')}
            className={`px-6 py-3 font-medium text-sm transition-colors border-b-2 ${
              activeTab === 'reservations'
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-secondary-600 hover:text-secondary-900'
            }`}
          >
            Reservations
            <span className="ml-2 badge badge-primary">{reservations.length}</span>
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`px-6 py-3 font-medium text-sm transition-colors border-b-2 ${
              activeTab === 'analytics'
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-secondary-600 hover:text-secondary-900'
            }`}
          >
            Analytics & Logs
          </button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Link
                to="/admin/developers"
                className="card p-6 hover:shadow-lg transition-shadow border-2 border-transparent hover:border-primary-200"
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-secondary-900">Manage Developers</h3>
                  <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <p className="text-sm text-secondary-600">
                  Invite developers and manage their projects
                </p>
              </Link>

              <Link
                to="/admin/create-project"
                className="card p-6 hover:shadow-lg transition-shadow border-2 border-transparent hover:border-primary-200"
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-secondary-900">Create Project</h3>
                  <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </div>
                <p className="text-sm text-secondary-600">
                  Set up a new project for a developer
                </p>
              </Link>

              <Link
                to="/admin/templates"
                className="card p-6 hover:shadow-lg transition-shadow border-2 border-transparent hover:border-primary-200"
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-secondary-900">Manage Templates</h3>
                  <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <p className="text-sm text-secondary-600">
                  Manage ASP contract templates
                </p>
              </Link>
            </div>

            {/* Recent Projects */}
            <div className="card p-6">
              <h2 className="text-xl font-semibold text-secondary-900 mb-4">Recent Projects</h2>
              {projectsLoading ? (
                <div className="flex justify-center py-8">
                  <div className="spinner text-primary-600"></div>
                </div>
              ) : projects.slice(0, 5).length === 0 ? (
                <p className="text-secondary-500">No projects yet.</p>
              ) : (
                <div className="space-y-3">
                  {projects.slice(0, 5).map((project) => (
                    <div key={project.id} className="flex items-center justify-between py-3 border-b border-secondary-100 last:border-0">
                      <div>
                        <Link to={`/projects/${project.id}`} className="font-medium text-secondary-900 hover:text-primary-600">
                          {project.name}
                        </Link>
                        <p className="text-sm text-secondary-600">
                          {project.developer?.company_name || project.developer?.full_name} • {project.location}
                        </p>
                      </div>
                      <span className={`badge ${
                        project.status === 'activated' ? 'badge-success' :
                        project.status === 'draft' ? 'badge-secondary' :
                        'badge-primary'
                      }`}>
                        {project.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Recent Reservations */}
            <div className="card p-6">
              <h2 className="text-xl font-semibold text-secondary-900 mb-4">Recent Reservations</h2>
              {reservationsLoading ? (
                <div className="flex justify-center py-8">
                  <div className="spinner text-primary-600"></div>
                </div>
              ) : reservations.slice(0, 5).length === 0 ? (
                <p className="text-secondary-500">No reservations yet.</p>
              ) : (
                <div className="space-y-3">
                  {reservations.slice(0, 5).map((reservation) => (
                    <div key={reservation.id} className="flex items-center justify-between py-3 border-b border-secondary-100 last:border-0">
                      <div>
                        <p className="font-medium text-secondary-900">
                          {reservation.unit?.project?.title} - Unit {reservation.unit?.unit_number}
                        </p>
                        <p className="text-sm text-secondary-600">
                          {reservation.buyer?.full_name} • {new Date(reservation.created_at).toLocaleDateString('en-NZ')}
                        </p>
                      </div>
                      <span className={`badge text-sm font-semibold ${
                        reservation.status === 'active' ? 'badge-success' :
                        reservation.status === 'converted' ? 'badge-primary' :
                        reservation.status === 'withdrawn' ? 'bg-orange-100 text-orange-800' :
                        reservation.status === 'expired' ? 'badge-error' :
                        'badge-secondary'
                      }`}>
                        {reservation.status === 'withdrawn' ? 'WITHDRAWN' :
                         reservation.status === 'expired' ? 'EXPIRED' :
                         reservation.status.toUpperCase()}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Projects Tab */}
        {activeTab === 'projects' && (
          <div className="card">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-secondary-50 border-b border-secondary-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">
                      Project
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">
                      Developer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">
                      Location
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">
                      Created
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-secondary-200">
                  {projects.map((project) => (
                    <tr key={project.id} className="hover:bg-secondary-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link to={`/projects/${project.id}`} className="font-medium text-secondary-900 hover:text-primary-600">
                          {project.name}
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-600">
                        {project.developer?.company_name || project.developer?.full_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-600">
                        {project.location}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`badge ${
                          project.status === 'activated' ? 'badge-success' :
                          project.status === 'draft' ? 'badge-secondary' :
                          'badge-primary'
                        }`}>
                          {project.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-600">
                        {new Date(project.created_at).toLocaleDateString('en-NZ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="card">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-secondary-50 border-b border-secondary-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">
                      Admin Level
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">
                      Joined
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-secondary-200">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-secondary-50">
                      <td className="px-6 py-4 whitespace-nowrap font-medium text-secondary-900">
                        {user.full_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-600">
                        {user.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`badge ${
                          user.role === 'admin' ? 'badge-error' :
                          user.role === 'developer' ? 'badge-primary' :
                          'badge-secondary'
                        }`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.admin_level && (
                          <span className={`badge ${
                            user.admin_level === 'super_admin' ? 'badge-error' :
                            'badge-warning'
                          }`}>
                            {user.admin_level === 'super_admin' ? 'Super Admin' : 'Admin'}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-600">
                        {new Date(user.created_at).toLocaleDateString('en-NZ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Reservations Tab */}
        {activeTab === 'reservations' && (
          <div className="card">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-secondary-50 border-b border-secondary-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">
                      Project / Unit
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">
                      Buyer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">
                      Expires
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-secondary-200">
                  {reservations.map((reservation) => (
                    <tr key={reservation.id} className="hover:bg-secondary-50">
                      <td className="px-6 py-4 whitespace-nowrap font-medium text-secondary-900">
                        {reservation.unit?.project?.title} - Unit {reservation.unit?.unit_number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-600">
                        {reservation.buyer?.full_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`badge text-sm font-semibold ${
                          reservation.status === 'active' ? 'badge-success' :
                          reservation.status === 'converted' ? 'badge-primary' :
                          reservation.status === 'withdrawn' ? 'bg-orange-100 text-orange-800' :
                          reservation.status === 'expired' ? 'badge-error' :
                          'badge-secondary'
                        }`}>
                          {reservation.status === 'withdrawn' ? 'WITHDRAWN' :
                           reservation.status === 'expired' ? 'EXPIRED' :
                           reservation.status.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-600">
                        {new Date(reservation.created_at).toLocaleDateString('en-NZ')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-600">
                        {reservation.status === 'active' && reservation.expires_at ? (
                          new Date(reservation.expires_at).toLocaleDateString('en-NZ')
                        ) : (reservation.status === 'withdrawn' || reservation.status === 'expired') && reservation.status_changed_at ? (
                          <span className="font-medium">
                            {reservation.status === 'withdrawn' ? 'Withdrawn: ' : 'Expired: '}
                            {new Date(reservation.status_changed_at).toLocaleDateString('en-NZ')}
                          </span>
                        ) : (
                          'N/A'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <AdminAnalyticsPanel />
        )}

        {/* Export Actions */}
        {activeTab !== 'analytics' && activeTab !== 'overview' && (
          <div className="mt-6 flex justify-end">
            <button
              onClick={() => {
                try {
                  if (activeTab === 'projects') {
                    exportProjectsToCSV(supabase)
                  } else if (activeTab === 'reservations') {
                    exportReservationsToCSV(supabase)
                  } else if (activeTab === 'users') {
                    const exportData = users.map(u => ({
                      id: u.id,
                      email: u.email,
                      full_name: u.full_name || '',
                      role: u.role,
                      company_name: u.company_name || '',
                      created_at: u.created_at,
                    }))
                    const { exportToCSV } = require('@/lib/csvExport')
                    exportToCSV(exportData, `users_${new Date().toISOString().split('T')[0]}.csv`)
                  }
                } catch (error) {
                  console.error('Export error:', error)
                  alert('Failed to export data')
                }
              }}
              className="btn-secondary px-6 py-2"
            >
              <svg className="w-5 h-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export to CSV
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
